import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
// Angular
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { isNotNullOrUndefined } from 'codelyzer/util/isNotNullOrUndefined';
import { CookieService } from 'ngx-cookie-service';
// RxJS
import { AsyncSubject, BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { AppState } from '../../../_reducers';
import { Logout } from '../../../user/_actions/auth.actions';
import { AuthModel } from '../../../user/_models/auth.model';
import { AuthTokenService } from '../../../user/_services/auth-token.service';
import { LayoutConfigService } from '../../layout';
import { LayoutUtilsService, MessageType } from './layout-utils.service';

/**
 * More information there => https://medium.com/@MetonymyQT/angular-http-interceptors-what-are-they-and-how-to-use-them-52e060321088
 */

@Injectable()
export class InterceptService implements HttpInterceptor {
  accessToken: string;
  refreshToken: string;


  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  // Refresh Token Subject tracks the current token, or is null if no token is currently
  // available (e.g. refresh pending).

  constructor(
    private layoutConfigService: LayoutConfigService,
    private cookieService: CookieService,
    private router: Router,
    private layoutUtilsService: LayoutUtilsService,
    private refreshService: AuthTokenService,
    private store: Store<AppState>
  ) {
    this.accessToken = sessionStorage.getItem(environment.authTokenKey);
    this.refreshToken = this.cookieService.get('jid');


  }

  // intercept request and add token
  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (request.headers.has('X-Bypass-Auth') && request.headers.get('X-Bypass-Auth') === 'flights') {
      // Let this request pass through unmodified
      return next.handle(request);
    }
    // tslint:disable-next-line:no-debugger
    if (request.url.includes('auth')) {
      request = this.addAuthenticationToken(request, false);
    } else {
      request = this.addAuthenticationToken(request);
    }

    const subject = new AsyncSubject<HttpEvent<any>>();

    return next.handle(request).pipe(
      tap((event: HttpEvent<any>) => {
        // Let HttpResponses pass through interceptor without interaction
        if (event instanceof HttpResponse) {
          subject.next(event);
          subject.complete();
        }
      }),
      catchError(error => {
        // if (
        //   error instanceof HttpErrorResponse &&
        //   error.status === 401
        // ) {
        //   return this.handle401Error(request, next);
        // }
        if (error.status === 0) {
          this.layoutUtilsService.showActionNotification(
            'Connection lost! You are not connected to internet.',
            MessageType.Read
          );
          return of(error);
        } else {
          return throwError(error);
        }
      })
    );
  }

  addAuthenticationToken(request, authRoute = true): HttpRequest<any> {
    const usesDirectApiHeaders = request.url.includes('/gen/utility/voucher/validate');

    if (usesDirectApiHeaders) {
      return request.clone({
        setHeaders: {
          'Content-Type': 'application/json'
        }
      });
    }

    const currency = this.layoutConfigService.getConfig('site.currency');
    const appName = this.layoutConfigService.getConfig('site.domain');
    const referral = localStorage.getItem('referral');

    return request.clone({
      setHeaders: {
        'Authorization': authRoute ? `Bearer ${this.accessToken}` : '',
        'Content-Type': 'application/json',
        'X-Client': 'x7Iski8qdYA1/Ol1QrxDDfVUWAePx1tsJWF7+KsAr5A=',
        'X-Currency': isNotNullOrUndefined(currency) ? currency : '',
        'X-Referral': isNotNullOrUndefined(referral) ? referral : '',
        'X-AppName': isNotNullOrUndefined(appName) ? appName : ''
      }
    });
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.refreshService.refreshTheToken().pipe(
        switchMap((auth: AuthModel) => {
          this.accessToken = auth.access_token;
          this.refreshToken = auth.session_token;
          sessionStorage.setItem(
            environment.authTokenKey,
            this.accessToken
          );
          this.cookieService.set(
            'jid',
            this.refreshToken,
            7,
            '/',
            '',
            environment.production,
            'Strict'
          );

          this.isRefreshing = false;
          this.refreshTokenSubject.next(auth);

          return next.handle(this.addAuthenticationToken(request));
        }),
        catchError(e => {
          // error
          this.isRefreshing = false;
          this.store.dispatch(new Logout());
          return throwError(e);
        })
      );
    }
    return this.refreshTokenSubject.pipe(
      filter(token => token != null),
      take(1),
      switchMap(_ => next.handle(this.addAuthenticationToken(request)))
    );
  }
}
